<script setup lang="ts">
import { computed, markRaw, ref, watch } from "vue";
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Home,
  LogOut,
  PlusCircle,
  Settings2,
  Sparkles,
  Users,
  ShieldCheck,
  Handshake,
} from "lucide-vue-next";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { useBriefStore } from "@/stores/briefs";

const props = defineProps<{ view: string }>();
const emit = defineEmits<{ changeView: [view: string] }>();
const { t, locale } = useI18n();
const session = useSessionStore();
const briefs = useBriefStore();
const isClient = computed(() => session.user?.role === "client");
const isPartner = computed(() => isClient.value && session.user?.account_type === "partner");
const compactMobileHeader = computed(() =>
  isClient.value && (props.view === "briefs" || props.view.startsWith("brief:")),
);
const mobileHeaderExpanded = ref(false);
const navItems = computed(() =>
  isClient.value
    ? isPartner.value ? [
        { id: "partner-service", label: t("shell.partnerService"), icon: markRaw(Handshake) },
        { id: "home", label: t("shell.home"), icon: markRaw(Home) },
        { id: "briefs", label: t("newBrief"), icon: markRaw(PlusCircle) },
      ] : [
        { id: "home", label: t("shell.home"), icon: markRaw(Home) },
        { id: "briefs", label: t("newBrief"), icon: markRaw(PlusCircle) },
      ]
      : [
        { id: "work", label: t("workbench"), icon: markRaw(BriefcaseBusiness) },
        { id: "collaboration", label: t("shell.collaboration"), icon: markRaw(ClipboardList) },
        { id: "partner-requests", label: t("shell.partnerRequests"), icon: markRaw(Handshake) },
        ...(session.user?.role === "admin"
          ? [
              { id: "admin-workflow", label: t("shell.dispatch"), icon: markRaw(ShieldCheck) },
              { id: "members", label: t("members"), icon: markRaw(Users) },
              { id: "model", label: t("model"), icon: markRaw(Settings2) },
              { id: "skills", label: "Skills", icon: markRaw(Sparkles) },
            ]
          : []),
      ],
);
watch(() => props.view, () => {
  mobileHeaderExpanded.value = false;
});
function changeLanguage(value: string) {
  locale.value = value;
  void session.setLocale(value);
}
async function removeBrief(id: number) { if (!window.confirm(t('shell.deleteBrief'))) return; await briefs.deleteBrief(id); if (props.view === `brief:${id}`) emit('changeView', 'home') }
</script>

<template>
  <div
    class="shell"
    :class="{
      'shell--detail': compactMobileHeader,
      'shell--header-expanded': mobileHeaderExpanded,
    }"
  >
    <header class="topbar">
      <button
        v-if="compactMobileHeader"
        class="mobile-header-toggle"
        type="button"
        :title="mobileHeaderExpanded ? t('shell.collapse') : t('shell.expand')"
        :aria-label="mobileHeaderExpanded ? t('shell.collapse') : t('shell.expand')"
        @click="mobileHeaderExpanded = !mobileHeaderExpanded"
      >
        <ChevronUp v-if="mobileHeaderExpanded" :size="18" />
        <ChevronDown v-else :size="18" />
      </button>
      <div class="brand">
        <img src="/jaohui-logo.webp" alt="Jaohui" /><b>{{ t("app") }}</b>
      </div>
      <div class="topbar__actions">
        <select
          v-if="isClient"
          class="locale"
          :value="locale"
          aria-label="Language"
          @change="changeLanguage(($event.target as HTMLSelectElement).value)"
        >
          <option value="zh-CN">中文</option>
          <option value="en">English</option>
          <option value="fr">Francais</option>
        </select>
        <span class="status">{{
          session.user?.role === "client"
            ? isPartner ? t("shell.partner") : t("shell.client")
            : session.user?.role === "staff"
              ? t("shell.staff")
              : t("shell.admin")
        }}</span>
        <span class="user-name">{{ session.user?.display_name }}</span>
        <button class="btn logout" :title="t('logout')" @click="session.logout"><LogOut :size="16" /><span>{{ t("logout") }}</span></button>
      </div>
    </header>
    <div class="shell__body">
      <nav class="nav" :style="{ '--mobile-nav-count': navItems.length }">
        <button
          v-for="item in navItems"
          :key="item.id"
          class="nav__item"
          :class="{ active: props.view === item.id }"
          @click="emit('changeView', item.id)"
        >
          <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
        </button>
        <section v-if="isClient" class="brief-nav">
          <small>{{ t("myBriefs") }}</small
          ><div v-for="brief in briefs.briefs" :key="brief.id" class="brief-nav__row"><button class="brief-nav__item" :class="{ active: props.view === `brief:${brief.id}` }" :title="brief.title" @click="emit('changeView', `brief:${brief.id}`)">{{ brief.title }}</button><button class="brief-nav__delete" :title="t('shell.deleteNamed', { title: brief.title })" @click="removeBrief(brief.id)">{{ t("common.delete") }}</button></div>
        </section>
      </nav>
      <main class="main"><slot /></main>
    </div>
  </div>
</template>

<style scoped>
.brief-nav {
  display: grid;
  gap: 4px;
  margin: 18px 4px 0;
  padding-top: 14px;
  border-top: 1px solid var(--line);
}
.brief-nav small {
  padding: 0 6px;
  color: var(--muted);
}
.brief-nav__item {
  overflow: hidden;
  border: 0;
  padding: 8px 6px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.brief-nav__row{display:flex;min-width:0}.brief-nav__row .brief-nav__item{flex:1;min-width:0}.brief-nav__delete{display:none;border:0;background:transparent;color:#b45b51;font-size:11px}.brief-nav__row:hover .brief-nav__delete{display:block}
.brief-nav__item:hover,
.brief-nav__item.active {
  background: #fff0e7;
  color: var(--brand-dark);
}
.shell {
  min-height: 100vh;
  --mobile-topbar-height: 64px;
}
.mobile-header-toggle{display:none}
.topbar {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px;
  background: #fff;
  border-bottom: 1px solid var(--line);
  gap: 12px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
}
.brand img {
  width: 30px;
  height: 30px;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 9px;
}
.locale {
  width: auto;
  border: 1px solid var(--line);
  padding: 5px 7px;
  background: #fff;
}
.shell__body {
  display: grid;
  grid-template-columns: 216px minmax(0, 1fr);
  min-height: calc(100vh - 64px);
}
.nav {
  padding: 16px 10px;
  border-right: 1px solid var(--line);
  background: #fbfcfc;
}
.nav__item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: 0;
  padding: 10px;
  text-align: left;
  background: transparent;
  color: var(--ink);
}
.nav__item.active {
  background: #fff0e7;
  color: var(--brand-dark);
  font-weight: 700;
}
.main {
  min-width: 0;
}
.user-name {
  font-size: 13px;
}
.logout{display:inline-flex;align-items:center;gap:6px}
@media (max-width: 700px) {
  .shell{padding-bottom:calc(62px + env(safe-area-inset-bottom));--mobile-topbar-height:56px}
  .topbar {
    height: auto;
    min-height: 56px;
    position:sticky;
    top:0;
    z-index:30;
    padding:8px 12px;
  }
  .brand b {
    font-size: 14px;
  }
  .shell--detail{--mobile-topbar-height:32px}
  .shell--detail .topbar{height:32px;min-height:32px;justify-content:center;padding:0 8px}
  .shell--detail .brand,.shell--detail .topbar__actions{display:none}
  .mobile-header-toggle{display:grid;width:40px;height:32px;place-items:center;border:0;background:transparent;color:var(--brand-dark)}
  .shell--detail.shell--header-expanded{--mobile-topbar-height:56px}
  .shell--detail.shell--header-expanded .topbar{display:grid;grid-template-columns:40px minmax(0,1fr) auto;height:auto;min-height:56px;justify-content:initial;padding:6px 10px}
  .shell--detail.shell--header-expanded .brand{display:flex;min-width:0}.shell--detail.shell--header-expanded .brand b{display:none}.shell--detail.shell--header-expanded .brand img{width:28px;height:28px}
  .shell--detail.shell--header-expanded .topbar__actions{display:flex}
  .user-name {
    display: none;
  }
  .shell__body {
    grid-template-columns: 1fr;
    min-height:calc(100dvh - var(--mobile-topbar-height) - 62px - env(safe-area-inset-bottom));
  }
  .nav {
    position:fixed;
    z-index:40;
    left:0;
    right:0;
    bottom:0;
    display:grid;
    grid-template-columns:repeat(var(--mobile-nav-count),minmax(0,1fr));
    gap:0;
    min-height:calc(62px + env(safe-area-inset-bottom));
    padding:5px 6px env(safe-area-inset-bottom);
    border:0;
    border-top:1px solid var(--line);
    background:#fff;
    box-shadow:0 -8px 24px rgba(24,34,44,.08);
  }
  .nav__item {
    display:grid;
    width:100%;
    min-width:0;
    min-height:52px;
    place-items:center;
    align-content:center;
    gap:2px;
    padding:4px 2px;
    font-size:11px;
  }
  .nav__item.active{background:transparent;color:var(--brand-dark)}
  .nav__item span{overflow:hidden;max-width:100%;text-overflow:ellipsis;white-space:nowrap}
  .brief-nav{display:none}
  .topbar__actions {
    gap: 6px;
  }
  .topbar__actions .btn {
    width:38px;
    min-height:38px;
    padding:0;
    justify-content:center;
  }
  .logout span,.topbar__actions>.status{display:none}
  .locale {
    max-width: 74px;
  }
  .main{min-height:calc(100dvh - var(--mobile-topbar-height) - 62px - env(safe-area-inset-bottom))}
}
</style>
